import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import AnimatedCard from '../components/AnimatedCard';
import AnimatedButton from '../components/AnimatedButton';
import LoadingSpinner from '../components/LoadingSpinner';
import Toast from '../components/Toast';
import api from '../api/axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

export default function AdminDiscounts() {
  const { isDark } = useTheme();
  const { t } = useLanguage();
  const [discounts, setDiscounts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [formData, setFormData] = useState({
    property_id: '',
    code: '',
    type: 'percentage',
    value: '',
    min_purchase: '',
    max_usage: '',
    valid_from: '',
    valid_until: '',
    is_active: true
  });

  useEffect(() => {
    fetchDiscounts();
  }, []);

  const fetchDiscounts = async () => {
    try {
      setLoading(true);
      const [discountsRes, propertiesRes] = await Promise.all([
        api.get('/discounts').catch(() => ({ data: [] })),
        api.get('/discounts-properties').catch(() => ({ data: [] }))
      ]);
      setDiscounts(discountsRes.data || []);
      setProperties(propertiesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      showToast('Error fetching data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submitted!');
    console.log('Form data:', formData);

    // Validasi manual
    if (!formData.code || !formData.value || !formData.valid_from || !formData.valid_until) {
      showToast('Mohon isi semua field yang wajib!', 'error');
      return;
    }

    try {
      const submitData = {
        ...formData,
        property_id: formData.property_id || null,
        min_purchase: formData.min_purchase || null,
        max_usage: formData.max_usage || null
      };

      console.log('Sending to API:', submitData);

      let response;
      if (editingDiscount) {
        response = await api.put(`/discounts/${editingDiscount.id}`, submitData);
        showToast('Diskon berhasil diupdate!');
      } else {
        response = await api.post('/discounts', submitData);
        showToast('Diskon berhasil ditambahkan!');
      }

      console.log('API Response:', response.data);
      fetchDiscounts();
      resetForm();
    } catch (error) {
      console.error('API Error:', error);
      console.error('Error details:', error.response?.data);
      showToast('Error: ' + (error.response?.data?.message || error.message), 'error');
    }
  };

  const handleDelete = async (id) => {
    if (confirm('Yakin ingin menghapus diskon ini?')) {
      try {
        await api.delete(`/discounts/${id}`);
        showToast('Diskon berhasil dihapus!');
        fetchDiscounts();
      } catch (error) {
        showToast('Error menghapus diskon', 'error');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      property_id: '',
      code: '',
      type: 'percentage',
      value: '',
      min_purchase: '',
      max_usage: '',
      valid_from: '',
      valid_until: '',
      is_active: true
    });
    setEditingDiscount(null);
    setShowForm(false);
  };

  const handleEdit = (discount) => {
    setFormData({
      property_id: discount.property_id || '',
      code: discount.code,
      type: discount.type,
      value: discount.value,
      min_purchase: discount.min_purchase || '',
      max_usage: discount.max_usage || '',
      valid_from: discount.valid_from,
      valid_until: discount.valid_until,
      is_active: discount.is_active
    });
    setEditingDiscount(discount);
    setShowForm(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className={`text-2xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} flex items-center`}>
            <i className="fas fa-tags mr-3 text-blue-500" />
            Manajemen Diskon & Promo
          </h1>
          <p className={`mt-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Kelola sistem diskon dan promo untuk meningkatkan penjualan
          </p>
        </div>
        <AnimatedButton
          onClick={() => setShowForm(!showForm)}
          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-3 rounded-xl shadow-lg"
        >
          <i className="fas fa-plus mr-2" />
          {showForm ? 'Tutup Form' : 'Tambah Diskon'}
        </AnimatedButton>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <AnimatedCard className={`p-6 ${isDark ? 'bg-gray-800/50' : 'bg-white/50'} backdrop-blur-sm border ${isDark ? 'border-gray-700/30' : 'border-white/30'} rounded-xl`}>
          <div className="flex items-center">
            <div className="p-3 bg-blue-500/20 rounded-lg">
              <i className="fas fa-tags text-blue-500 text-xl" />
            </div>
            <div className="ml-4">
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Diskon</p>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{discounts.length}</p>
            </div>
          </div>
        </AnimatedCard>

        <AnimatedCard className={`p-6 ${isDark ? 'bg-gray-800/50' : 'bg-white/50'} backdrop-blur-sm border ${isDark ? 'border-gray-700/30' : 'border-white/30'} rounded-xl`}>
          <div className="flex items-center">
            <div className="p-3 bg-green-500/20 rounded-lg">
              <i className="fas fa-check-circle text-green-500 text-xl" />
            </div>
            <div className="ml-4">
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Aktif</p>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {discounts.filter(d => d.is_active).length}
              </p>
            </div>
          </div>
        </AnimatedCard>

        <AnimatedCard className={`p-6 ${isDark ? 'bg-gray-800/50' : 'bg-white/50'} backdrop-blur-sm border ${isDark ? 'border-gray-700/30' : 'border-white/30'} rounded-xl`}>
          <div className="flex items-center">
            <div className="p-3 bg-yellow-500/20 rounded-lg">
              <i className="fas fa-percentage text-yellow-500 text-xl" />
            </div>
            <div className="ml-4">
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Persentase</p>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {discounts.filter(d => d.type === 'percentage').length}
              </p>
            </div>
          </div>
        </AnimatedCard>

        <AnimatedCard className={`p-6 ${isDark ? 'bg-gray-800/50' : 'bg-white/50'} backdrop-blur-sm border ${isDark ? 'border-gray-700/30' : 'border-white/30'} rounded-xl`}>
          <div className="flex items-center">
            <div className="p-3 bg-purple-500/20 rounded-lg">
              <i className="fas fa-coins text-purple-500 text-xl" />
            </div>
            <div className="ml-4">
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Nominal</p>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {discounts.filter(d => d.type === 'fixed').length}
              </p>
            </div>
          </div>
        </AnimatedCard>
      </div>

      {/* Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <AnimatedCard className={`p-6 ${isDark ? 'bg-gray-800/50' : 'bg-white/50'} backdrop-blur-sm border ${isDark ? 'border-gray-700/30' : 'border-white/30'} rounded-xl`}>
              <h3 className={`text-xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {editingDiscount ? 'Edit Diskon' : 'Tambah Diskon Baru'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Unit/Properti
                    </label>
                    <select
                      value={formData.property_id}
                      onChange={(e) => setFormData({ ...formData, property_id: e.target.value })}
                      className={`w-full px-4 py-3 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    >
                      <option value="">Semua Unit (Global)</option>
                      {properties.map(property => (
                        <option key={property.id} value={property.id}>{property.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Kode Diskon *
                    </label>
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      required
                      className={`w-full px-4 py-3 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      placeholder="DISKON20"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Tipe Diskon *
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      className={`w-full px-4 py-3 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    >
                      <option value="percentage">Persentase (%)</option>
                      <option value="fixed">Nominal Tetap (Rp)</option>
                    </select>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Nilai Diskon *
                    </label>
                    <input
                      type="number"
                      value={formData.value}
                      onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                      required
                      className={`w-full px-4 py-3 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      placeholder={formData.type === 'percentage' ? '20' : '100000'}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Minimal Pembelian
                    </label>
                    <input
                      type="number"
                      value={formData.min_purchase}
                      onChange={(e) => setFormData({ ...formData, min_purchase: e.target.value })}
                      className={`w-full px-4 py-3 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      placeholder="500000"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Maksimal Penggunaan
                    </label>
                    <input
                      type="number"
                      value={formData.max_usage}
                      onChange={(e) => setFormData({ ...formData, max_usage: e.target.value })}
                      className={`w-full px-4 py-3 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      placeholder="100"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Berlaku Dari *
                    </label>
                    <DatePicker
                      selected={formData.valid_from ? new Date(formData.valid_from) : null}
                      onChange={(date) => setFormData({ ...formData, valid_from: date ? date.toISOString().split('T')[0] : '' })}
                      dateFormat="dd/MM/yyyy"
                      required
                      className={`w-full px-4 py-3 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      placeholderText="Pilih tanggal"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Berlaku Sampai *
                    </label>
                    <DatePicker
                      selected={formData.valid_until ? new Date(formData.valid_until) : null}
                      onChange={(date) => setFormData({ ...formData, valid_until: date ? date.toISOString().split('T')[0] : '' })}
                      dateFormat="dd/MM/yyyy"
                      minDate={formData.valid_from ? new Date(formData.valid_from) : null}
                      required
                      className={`w-full px-4 py-3 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      placeholderText="Pilih tanggal"
                    />
                  </div>

                  <div className="flex items-center">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        className="sr-only"
                      />
                      <div className={`relative w-11 h-6 rounded-full transition-colors ${formData.is_active ? 'bg-blue-500' : isDark ? 'bg-gray-600' : 'bg-gray-300'}`}>
                        <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${formData.is_active ? 'translate-x-5' : 'translate-x-0'}`} />
                      </div>
                      <span className={`ml-3 text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        Status Aktif
                      </span>
                    </label>
                  </div>
                </div>

                <div className="flex space-x-4">
                  <button
                    type="submit"
                    onClick={(e) => {
                      console.log('Submit button clicked');
                      console.log('Form data:', formData);
                    }}
                    className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-6 py-3 rounded-xl"
                  >
                    <i className="fas fa-save mr-2" />
                    {editingDiscount ? 'Update Diskon' : 'Simpan Diskon'}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className={`px-6 py-3 rounded-xl border ${isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                  >
                    <i className="fas fa-times mr-2" />
                    Batal
                  </button>
                </div>
              </form>
            </AnimatedCard>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Discounts Table */}
      <AnimatedCard className={`${isDark ? 'bg-gray-800/50' : 'bg-white/50'} backdrop-blur-sm border ${isDark ? 'border-gray-700/30' : 'border-white/30'} rounded-xl overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`${isDark ? 'bg-gray-700/50' : 'bg-gray-50/50'}`}>
              <tr>
                <th className={`px-6 py-4 text-left text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>Kode</th>
                <th className={`px-6 py-4 text-left text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>Unit</th>
                <th className={`px-6 py-4 text-left text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>Tipe</th>
                <th className={`px-6 py-4 text-left text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>Nilai</th>
                <th className={`px-6 py-4 text-left text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>Min. Beli</th>
                <th className={`px-6 py-4 text-left text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>Penggunaan</th>
                <th className={`px-6 py-4 text-left text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>Periode</th>
                <th className={`px-6 py-4 text-left text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>Status</th>
                <th className={`px-6 py-4 text-left text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>Aksi</th>
              </tr>
            </thead>
            <tbody className={`${isDark ? 'bg-gray-800/30' : 'bg-white/30'} divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {discounts.map((discount, index) => (
                <motion.tr
                  key={discount.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`hover:${isDark ? 'bg-gray-700/30' : 'bg-gray-50/30'} transition-colors`}
                >
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {discount.code}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${discount.property_id
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-800'
                      }`}>
                      {discount.property?.name || 'Global'}
                    </span>
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${discount.type === 'percentage'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-purple-100 text-purple-800'
                      }`}>
                      {discount.type === 'percentage' ? 'Persentase' : 'Nominal'}
                    </span>
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    {discount.type === 'percentage' ? `${discount.value}%` : `Rp ${discount.value.toLocaleString()}`}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    {discount.min_purchase ? `Rp ${discount.min_purchase.toLocaleString()}` : '-'}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    {discount.used_count || 0} / {discount.max_usage || '∞'}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    <div className="text-xs">
                      <div>{discount.valid_from}</div>
                      <div>s/d {discount.valid_until}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${discount.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                      }`}>
                      {discount.is_active ? 'Aktif' : 'Nonaktif'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <AnimatedButton
                      onClick={() => handleEdit(discount)}
                      className="text-blue-600 hover:text-blue-900 p-2 rounded-lg hover:bg-blue-50"
                    >
                      <i className="fas fa-edit" />
                    </AnimatedButton>
                    <AnimatedButton
                      onClick={() => handleDelete(discount.id)}
                      className="text-red-600 hover:text-red-900 p-2 rounded-lg hover:bg-red-50"
                    >
                      <i className="fas fa-trash" />
                    </AnimatedButton>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
          {discounts.length === 0 && (
            <div className="text-center py-12">
              <i className={`fas fa-tags text-4xl ${isDark ? 'text-gray-600' : 'text-gray-400'} mb-4`} />
              <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Belum ada diskon. Klik "Tambah Diskon" untuk membuat yang pertama.
              </p>
            </div>
          )}
        </div>
      </AnimatedCard>

      {/* Toast */}
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ show: false, message: '', type: 'success' })}
      />
    </div>
  );
}